import { getToken, onMessage } from "firebase/messaging";
import { initializeMessaging, getMessagingInstance, VAPID_KEY } from "../config/firebase.config";
import { encrypt_res } from "../components/shares/GlobalFunction";
import { API_URL, SECRET_SESSION } from "../constants";
import axiosInstance from "../components/shares/axiosConfig";

class FirebaseService {
  constructor() {
    this.initialized = false;
    this.initializationPromise = null;
    this.permissionPromise = null;
    this.tokenPromise = null;
    this.messageListener = null;
    this.messageCallback = null;
  }

  async initialize() {
    // If already initialized, return immediately
    if (this.initialized) {
      return;
    }

    // If initialization is in progress, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Create a new initialization promise
    this.initializationPromise = (async () => {
      try {
        console.log('🔥 Initializing Firebase Messaging...');
        const messaging = await initializeMessaging();
        
        if (!messaging) {
          console.error('❌ Failed to initialize Firebase Messaging');
          throw new Error('Firebase Messaging initialization failed');
        }

        if ('serviceWorker' in navigator) {
          try {
            console.log('👷 Registering service worker...');
            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('✅ Service Worker registered:', registration);
          } catch (err) {
            console.error('❌ Service Worker registration failed:', err);
            throw err;
          }
        } else {
          console.error('❌ Service Worker not supported in this browser');
          throw new Error('Service Worker not supported');
        }

        this.initialized = true;
        console.log('✅ Firebase Service initialized successfully');
      } catch (error) {
        this.initializationPromise = null;
        throw error;
      }
    })();

    return this.initializationPromise;
  }

  async requestNotificationPermission() {
    try {
      await this.initialize();
      
      // If permission request is in progress, return the existing promise
      if (this.permissionPromise) {
        return this.permissionPromise;
      }

      // Create a new permission request promise
      this.permissionPromise = (async () => {
        console.log('🔔 Requesting notification permission...');
        const permission = await Notification.requestPermission();
        console.log('🔔 Notification permission status:', permission);
        
        if (permission === 'granted') {
          // Get FCM token
          const token = await this.getFCMToken();
          return token;
        }
        throw new Error('Notification permission denied');
      })();

      return this.permissionPromise;
    } catch (error) {
      this.permissionPromise = null;
      console.error('❌ Error requesting notification permission:', error);
      throw error;
    }
  }

  async getFCMToken() {
    try {
      // If token request is in progress, return the existing promise
      if (this.tokenPromise) {
        return this.tokenPromise;
      }

      // Create a new token request promise
      this.tokenPromise = (async () => {
        const messaging = getMessagingInstance();
        if (!messaging) {
          throw new Error('Firebase Messaging not initialized');
        }

        console.log('🔑 Getting FCM token...');
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY
        });
        console.log('✅ FCM token obtained:', token.substring(0, 20) + '...');
        return token;
      })();

      return this.tokenPromise;
    } catch (error) {
      this.tokenPromise = null;
      console.error('❌ Error getting FCM token:', error);
      throw error;
    }
  }

  async registerTokenWithBackend(userId, fcmToken) {
    try {
      console.log('💾 Registering FCM token with backend for user:', userId);
      const payload = {
        id_siswa: userId,
        fcm_token: fcmToken
      };

      const encryptedPayload = encrypt_res(JSON.stringify(payload), SECRET_SESSION);
      console.log('🔐 Payload encrypted, making API call...');

      const response = await axiosInstance.post(API_URL + "/services/fcm/token", {
        data: encryptedPayload
      });
      console.log('✅ Token registered successfully with backend');
      return response.data;
    } catch (error) {
      console.error('❌ Error registering FCM token with backend:', error);
      throw error;
    }
  }

  async removeTokenFromBackend(userId) {
    try {
      console.log('🗑️ Removing FCM token from backend for user:', userId);
      const payload = {
        id_siswa: userId
      };

      const encryptedPayload = encrypt_res(JSON.stringify(payload), SECRET_SESSION);
      console.log('🔐 Payload encrypted, making API call...');

      const response = await axiosInstance.delete(API_URL + "/services/fcm/token", {
        data: { data: encryptedPayload }
      });
      console.log('✅ Token removed successfully from backend');
      return response.data;
    } catch (error) {
      console.error('❌ Error removing FCM token from backend:', error);
      throw error;
    }
  }

  setupMessageListener(callback) {
    const messaging = getMessagingInstance();
    if (!messaging) {
      console.error('❌ Cannot setup listener: Firebase Messaging not initialized');
      throw new Error('Firebase Messaging not initialized');
    }

    // If we already have a listener with the same callback, return the existing unsubscribe function
    if (this.messageListener && this.messageCallback === callback) {
      return this.messageListener;
    }

    // If we have an existing listener with a different callback, remove it first
    if (this.messageListener) {
      this.messageListener();
      this.messageListener = null;
    }

    console.log('👂 Setting up FCM message listener...');
    this.messageCallback = callback;
    this.messageListener = onMessage(messaging, (payload) => {
      console.log('📨 Message received in foreground:', payload);
      
      // Handle different message types
      if (payload.data?.action === 'STOP_TEST') {
        console.log('🛑 Received STOP_TEST action');
        callback({
          type: 'STOP_TEST',
          message: 'Anda telah dihentikan dari ujian',
          data: payload.data
        });
      } else if (payload.data?.action === 'RESET_LOGIN') {
        console.log('🔄 Received RESET_LOGIN action');
        callback({
          type: 'RESET_LOGIN',
          message: 'Anda sedang request reset login',
          data: payload.data
        });
      } else if (payload.data?.action === 'SUPERVISOR_MESSAGE') {
        console.log('📝 Received SUPERVISOR_MESSAGE action');
        callback({
          type: 'SUPERVISOR_MESSAGE',
          title: payload.notification?.title || 'Pesan dari Pengawas',
          message: payload.data.message || payload.notification?.body || 'Pesan dari pengawas',
          data: payload.data
        });
      }
    });

    return this.messageListener;
  }
}

export default new FirebaseService(); 